import { useCallback, useContext, useEffect, useState } from 'react';

import { PORTFOLIO_TABS_NAME } from '../../../constants/reports/reports-data';
import { GlobalContext } from '../../../providers/global-provider';
import { PNLContext } from '../../../providers/pnl-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../providers/portfolio-management';
import { PositionsContext } from '../../../providers/positions/positions-provider';
import { RiskFiltersContext } from '../../../providers/risk-management/risk-filters-provider';
import {
  RiskManagementContext,
  riskTypes,
} from '../../../providers/risk-management/risk-management-provider';
import { StrikeReportContext } from '../../../providers/strike-report';
import { TransactionsContext } from '../../../providers/transactions/transactions-provider';

import { useFreezeSpotValues } from './use-freeze-spot-values';

export const usePortfolioCalculatePerAccount = () => {
  const [isAccountCalculated, setIsAccountCalculate] = useState(false);
  const [triggerRecalculate, setTriggerRecalculate] = useState(false);
  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    filters: { riskToken },
  } = useContext(RiskFiltersContext);
  const {
    mapDataAccounts,
    selectedAccountOption,
    selectedDataDisplayOption,
    handleCalculateAccountStatus,
    clearAccountData,
    isDataAccountEmpty,
    getDataDetailsAccount,
    riskSettingsSelected,
    isPortfolioLoading,
    spotOverrideValue,
  } = useContext(PortfolioManagementContext);
  const { getData: getDataSpot } = useContext(RiskManagementContext);
  const {
    getDataPnlLegacy,
    getDataPnl,
    filters: { pnlRiskToken },
  } = useContext(PNLContext);
  const { getData: getDataPositionsAndOrders } = useContext(PositionsContext);
  const { getData: getDataTransactions } = useContext(TransactionsContext);
  const { getStrikeReportData } = useContext(StrikeReportContext);
  const { updateFreezeSpotMapAndCallGetData } = useFreezeSpotValues();

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;
  const isUpdateFreezeSpotMap =
    riskSettingsSelected.isFreezeSpotOverride && spotOverrideValue !== '';

  const handleDataFetch = useCallback(
    (type: any) => {
      switch (type) {
        case PORTFOLIO_TABS_NAME.spotLadder:
          if (isUpdateFreezeSpotMap) {
            updateFreezeSpotMapAndCallGetData({
              accountOption: selectedAccountOption,
              riskType: riskTypes.spotLadder,
            });
          } else {
            getDataSpot({
              account: selectedAccountOption,
              dataParent: riskTypes.spotLadder,
              riskTokenParam: riskToken,
              riskSettings: riskSettingsSelected,
            });
          }
          break;
        case PORTFOLIO_TABS_NAME.spotGrid:
          if (isUpdateFreezeSpotMap) {
            updateFreezeSpotMapAndCallGetData({
              accountOption: selectedAccountOption,
              riskType: riskTypes.spotGrid,
            });
          } else {
            getDataSpot({
              account: selectedAccountOption,
              dataParent: riskTypes.spotGrid,
              riskTokenParam: riskToken,
              riskSettings: riskSettingsSelected,
            });
          }
          break;
        case PORTFOLIO_TABS_NAME.spotTime:
          if (isUpdateFreezeSpotMap) {
            updateFreezeSpotMapAndCallGetData({
              accountOption: selectedAccountOption,
              riskType: riskTypes.spotTime,
            });
          } else {
            getDataSpot({
              account: selectedAccountOption,
              dataParent: riskTypes.spotTime,
              riskTokenParam: riskToken,
              riskSettings: riskSettingsSelected,
            });
          }
          break;
        case PORTFOLIO_TABS_NAME.pnlLegacy:
          getDataPnlLegacy(selectedAccountOption, pnlRiskToken.toLowerCase());
          break;
        case PORTFOLIO_TABS_NAME.pnl:
          getDataPnl({
            accounts: selectedAccountOption,
            periodDate: [],
            riskToken: pnlRiskToken.toLowerCase(),
          });
          break;
        case PORTFOLIO_TABS_NAME.positionsAndOrders:
          getDataPositionsAndOrders(selectedAccountOption);
          break;
        case PORTFOLIO_TABS_NAME.transactions:
          getDataTransactions(selectedAccountOption);
          break;
        case PORTFOLIO_TABS_NAME.strikeReport:
          getStrikeReportData({
            accounts: selectedAccountOption,
          });
          break;
        default:
          break;
      }
    },
    [
      getDataSpot,
      getDataPnlLegacy,
      getDataPositionsAndOrders,
      getDataTransactions,
      getStrikeReportData,
      selectedAccountOption,
      riskSettingsSelected,
      riskToken,
      pnlRiskToken,
      spotOverrideValue,
    ],
  );

  const handleRecalculatePortfolioCurrentTab = useCallback(() => {
    if (!isDataAccountEmpty(selectedAccountOption) || !triggerRecalculate) return;

    handleDataFetch(portfolioCurrentTab);
    setTriggerRecalculate(false);
  }, [selectedAccountOption, portfolioCurrentTab, triggerRecalculate, handleDataFetch]);

  const handlePortfolioCalculate = useCallback(() => {
    if (!selectedDataDisplayOption || !selectedAccountOption) return;

    if (!isAccountCalculated) {
      handleCalculateAccountStatus(selectedAccountOption.name);

      if (isUpdateFreezeSpotMap) {
        updateFreezeSpotMapAndCallGetData({
          accountOption: selectedAccountOption,
          riskType: riskTypes.spotLadder,
        });
      } else {
        getDataSpot({
          account: selectedAccountOption,
          dataParent: riskTypes.spotLadder,
          riskTokenParam: riskToken,
          riskSettings: riskSettingsSelected,
        });
      }
      return;
    }

    clearAccountData(selectedAccountOption);
    setTriggerRecalculate(true);
  }, [
    selectedDataDisplayOption,
    selectedAccountOption,
    isAccountCalculated,
    portfolioCurrentTab,
    riskSettingsSelected,
    isDataDisplayPerAccount,
    riskToken,
    spotOverrideValue,
  ]);

  useEffect(() => {
    if (!isDataDisplayPerAccount) return;

    const detailsAccount = getDataDetailsAccount(selectedAccountOption);

    if (detailsAccount?.isCalculated) {
      setIsAccountCalculate(true);
    } else {
      setIsAccountCalculate(false);
    }
  }, [selectedAccountOption, isPortfolioLoading, isDataDisplayPerAccount]);

  useEffect(() => {
    if (!isDataDisplayPerAccount) return;

    if (triggerRecalculate && isDataAccountEmpty(selectedAccountOption)) {
      handleRecalculatePortfolioCurrentTab();
    }
  }, [
    mapDataAccounts,
    triggerRecalculate,
    selectedAccountOption,
    handleRecalculatePortfolioCurrentTab,
  ]);

  return {
    handlePortfolioCalculate,
    isAccountCalculated,
  };
};
